import * as React from "react";
import { observer } from "mobx-react";
import { UitkImage, UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkCardAloha, UitkCardAlohaContentSection, UitkCardAlohaLink } from "uitk-react-cards";
import { DestinationSuggestionsProps } from "../typings";
import { Destination } from "src/typings/microserviceModels/destination-suggestions-flex-module.d.ts";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { useLocalization } from "@shared-ui/localization-context";
import { withMabTracker, MabTrackEvent } from "components/utility/analytics/MabTracker";
import { UitkHeading, UitkSubheading } from "uitk-react-text";

const EmbeddingRedesignCard = withMabTracker(
  ({
    destination,
    cardIndex,
    mabTrack,
  }: {
    destination: Destination;
    cardIndex: number;
    mabTrack?: (x: MabTrackEvent) => null;
  }) => {
    const rfrrId = cardIndex + 1;

    const cardClicked = mabTrack
      ? () => {
          const mabTrackData: MabTrackEvent = {
            moduleName: "embedding",
            eventTarget: "embedding",
            eventType: "homepageClick.nonPath",
            rfrr: rfrrId.toString(),
          };
          mabTrack(mabTrackData);
        }
      : undefined;

    return (
      <>
        <UitkCardAloha className="EmbeddingCard uitk-flex-justify-content-flex-start uitk-flex-column">
          <UitkCardAlohaContentSection padded={false}>
            <UitkFigure ratio={UitkFigureAspectRatioType.R16_9} className="uitk-card-aloha-roundcorner-all">
              <UitkImage src={destination.destinationImg} alt={destination.imgAlt} />
            </UitkFigure>
            <UitkCardAlohaLink>
              <TrackedLink
                moduleName="Embedding"
                action={Action.CLICK}
                href={destination.url}
                rfrr={rfrrId}
                onClick={cardClicked}
              >
                {destination.name}
              </TrackedLink>
            </UitkCardAlohaLink>
          </UitkCardAlohaContentSection>
          <div className="uitk-card-content uitk-cell all-t-padding-one">
            <h3 className="uitk-type-heading-400">{destination.name}</h3>
          </div>
        </UitkCardAloha>
      </>
    );
  }
);

export const EmbeddingRedesign = observer(({ props }: { props: DestinationSuggestionsProps }) => {
  const keyHelper = new ItemKeyHelper(props.flexComponent.id);
  const { formatText } = useLocalization();
  const title = formatText("destinationSuggestions.embedding.title.v2");
  const subtitle = formatText("destinationSuggestions.embedding.title");

  return (
    <div
      data-testid="embeddingRedesign"
      className="uitk-cell all-b-margin-three"
      id={props.flexComponent.id}
      data-fm={props.flexComponent.fmId}
    >
      <div className="all-y-padding-three">
        <UitkHeading level={2} size={700}>
          {title}
        </UitkHeading>
        <UitkSubheading level={3}>{subtitle}</UitkSubheading>
      </div>
      <IsomorphicCarousel
        className="carouselComponent"
        itemsVisible={{
          lg: 4,
          md: 4,
          sm: 2,
        }}
        buttonText={{
          nextButton: formatText("carousel.item.next"),
          prevButton: formatText("carousel.item.prev"),
        }}
        pageBy={1}
        peekWidth={0}
      >
        {props.flexComponent.model.destinations.map((destination, index) => {
          return (
            <div className="uitk-height-item uitk-cell" key={keyHelper.next()}>
              <EmbeddingRedesignCard destination={destination} cardIndex={index} mabTrack={props.mabTrack} />
            </div>
          );
        })}
      </IsomorphicCarousel>
    </div>
  );
});
(EmbeddingRedesign as React.SFC).displayName = "EmbeddingRedesignView";
